import React from 'react';
import { Ban, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { deactivatePharmacyOptions, reactivatePharmacyOptions } from '@generated';
import { createQueryDataTable, type PropsOfQueryDataTable, type RowOfQueryDataTable } from '~/shared/data-tables/query/QueryDataTable';
import { Prompt } from '~/shared/components/Prompt';
import { RenderAddressCell } from '~/shared/data-tables/column-types/AddressColumn';
import { ActiveColumnCell } from '~/shared/data-tables/column-types/ActiveColumn';
import { formatPhoneNumber } from '~/util/functions';
import { useMutation } from '~/util/http/useMutation';
import { getSearchQueryKey } from '~/util/http/useSearch';
import { Button } from '~/shared/components/buttons/Button';
import { TableActions } from '~/shared/data-tables/TableActions';

const PharmaciesDataTable = createQueryDataTable('pharmacy');
type RowType = RowOfQueryDataTable<typeof PharmaciesDataTable>;
type TableProps = PropsOfQueryDataTable<typeof PharmaciesDataTable>;

function ActionsCell({ row }: { row: RowType }) {
	const navigate = useNavigate();

	const deactivatePharmacy = useMutation({
		...deactivatePharmacyOptions,
		invalidateQueryKeys: [getSearchQueryKey('pharmacy')]
	});

	const reactivatePharmacy = useMutation({
		...reactivatePharmacyOptions,
		invalidateQueryKeys: [getSearchQueryKey('pharmacy')]
	});

	return (
		<TableActions>
			{row.active ? (
				<>
					<TableActions.ViewButton aria-label="Edit Pharmacy" onClick={() => navigate(`edit/${row.id}`)} />
					<Button
						Icon={Ban}
						aria-label="Deactivate Pharmacy"
						intent="destructive"
						appearance="ghost"
						renderModal={closeModal => (
							<Prompt
								title="Deactivate Pharmacy"
								text="Are you sure you want to deactivate this pharmacy?"
								onAction={() => {
									deactivatePharmacy.mutateAsync({ pharmacyId: row.id });
									closeModal();
								}}
								onCancel={closeModal}
							/>
						)}
					/>
				</>
			) : (
				<Button
					Icon={Eye}
					aria-label="Reactivate Pharmacy"
					appearance="ghost"
					renderModal={closeModal => (
						<Prompt
							title="Reactivate Pharmacy"
							text="Are you sure you want to reactivate this pharmacy?"
							onAction={() => {
								reactivatePharmacy.mutateAsync({ pharmacyId: row.id });
								closeModal();
							}}
							onCancel={closeModal}
						/>
					)}
				/>
			)}
		</TableActions>
	);
}

function PharmacyNameCell({ row }: { row: RowType }) {
	return (
		<div className="flex flex-col">
			<div className="flex items-center gap-2">
				<span className="text-sm font-semibold text-gray-900">{row.name}</span>
			</div>
			{row.notes && <p className="mt-1 max-w-xs truncate text-xs text-gray-500">{row.notes}</p>}
		</div>
	);
}

const columnDefinitions = PharmaciesDataTable.columns([
	{
		header: 'Pharmacy Name',
		key: 'name',
		sortable: true,
		width: 'w-[300px]',
		renderCell: row => <PharmacyNameCell row={row} />
	},
	{
		header: 'Address',
		key: 'addressOne',
		width: 'w-[200px]',
		renderCell: row => <RenderAddressCell address={{ addressOne: row.addressOne, city: row.city, state: row.state, zipCode: row.zipCode }} />
	},
	{
		header: 'Phone Number',
		key: 'phoneNumber',
		type: 'phone'
	},
	{
		header: 'Fax Number',
		key: 'faxNumber',
		renderCell: row => (row.faxNumber ? formatPhoneNumber(row.faxNumber) : 'N/A')
	},
	{
		header: 'Pharmacy Hours',
		key: 'hoursOfOperation'
	},
	{
		header: 'Active',
		key: 'active',
		width: 'w-[100px]',
		renderCell: row => <ActiveColumnCell active={row.active} />
	},
	{
		header: 'Actions',
		key: 'actions',
		width: 'w-[100px]',
		renderCell: row => <ActionsCell row={row} />
	}
]);

export function PharmacyTable(props: TableProps) {
	return (
		<PharmaciesDataTable.Table
			columns={columnDefinitions}
			emptyMessage="No pharmacies found."
			getMuteRowOpacity={row => !row.active}
			{...props}
		/>
	);
}
